import * as React from "react";
import { useId } from "react-id-generator";

import {
  ITextInputElement,
  StringGenerator,
  TextInputElement,
} from "@graffer-inc/form-schema";
import {
  TenantProcessResultInputElementsValidator,
  TenantProcessResultValidationConstant,
} from "@graffer-inc/cloud-gov-core";

import {
  ProcedureDetailType,
  ProcedureEntity,
} from "../../../../../application/domain/procedures/procedure-entity";
import { TenantProcessResultInputElementsEditor } from "./input-elements-editor";
import { reportUnexpectedError } from "@graffer-inc/smart-apply";
import { UpdateButton } from "../update-button";
import { Toggle } from "@graffer-inc/console-design-kit";

type TenantProcessResultSettingProps = {
  procedure: ProcedureEntity;
  onDraftUpdate: (draft: ProcedureDetailType) => void;
};

export const TenantProcessResultSettingPage: React.FC<
  TenantProcessResultSettingProps
> = ({ procedure, onDraftUpdate }) => {
  const setting = procedure.getLiveDetail().template.tenantProcessResultSetting;
  const [enableSetting, setEnableSetting] = React.useState<boolean>(!!setting);
  const [inputElements, setInputElements] = React.useState<ITextInputElement[]>(
    setting ? setting.inputElements : [],
  );
  const errors = new TenantProcessResultInputElementsValidator(
    inputElements,
  ).validateDeep();

  const [enableTenantProcessResultSettingId] = useId();

  const updateSetting = React.useCallback(async () => {
    try {
      const detail = procedure.getLiveDetail();
      onDraftUpdate({
        ...detail,
        template: {
          ...detail.template,
          tenantProcessResultSetting: enableSetting
            ? {
                inputElements,
              }
            : undefined,
        },
      });
    } catch (e) {
      reportUnexpectedError(e);
    }
  }, [procedure, onDraftUpdate, enableSetting, inputElements]);

  return (
    <>
      <div className="columns">
        <div className="column has-text-left is-four-fifths">
          <div className="field">
            <Toggle
              id={enableTenantProcessResultSettingId}
              label={"管理情報"}
              description={
                "設定をONにすると、職員がそれぞれの申請に対して、受験番号や個別の情報といった管理情報を登録できるようになります。\n手続きの公開後には設定できません。管理情報は申請者からは閲覧できません。"
              }
              checked={enableSetting}
              onChange={() => {
                setEnableSetting(!enableSetting);
              }}
              disabled={procedure.isPublished()}
            />
          </div>
        </div>
        <div className="column has-text-right">
          <div className="level-right buttons">
            <UpdateButton
              procedure={procedure}
              onClick={updateSetting}
              disabled={errors.length > 0}
            />
          </div>
        </div>
      </div>
      {enableSetting && (
        <TenantProcessResultInputElementsEditor
          isReadonly={procedure.isPublished()}
          inputElements={inputElements}
          maxInputElements={
            TenantProcessResultValidationConstant.TOTAL_MAX_INPUT_ELEMENTS
          }
          onUpdate={(index, updated) => {
            setInputElements(
              inputElements.map((e, idx) => (idx !== index ? e : updated)),
            );
          }}
          onDelete={(index) =>
            setInputElements(inputElements.filter((_, i) => i !== index))
          }
          onCreate={() => {
            setInputElements([
              ...inputElements,
              TextInputElement.create({
                id: StringGenerator.generateUnique(
                  inputElements.map((e) => e.id),
                ),
                required: false,
                label: { shouldDisplay: true, text: "" },
                description: { text: "", shouldDisplay: true },
                placeholder: "",
              }),
            ]);
          }}
        />
      )}
    </>
  );
};
